import { useBlocksConfig, useSyncer } from "@pathwright/blocks-engine"
import { sansSerifStyles } from "@pathwright/blocks/src/ui/typography"
import {
  LazyVideoPlayer,
  MediaManager,
  MuxVideoEditor
} from "@pathwright/legacy-media-manager"
import { MuxDownloadButton } from "@pathwright/media-client"
import Button from "@pathwright/ui/src/components/button/Button"
import { Content } from "@pathwright/ui/src/components/content/Content"
import Text from "@pathwright/ui/src/components/ui/Text"
import React from "react"
import styled from "styled-components"
import AllowDownload from "../../utils/AllowDownload"
import { Row, VideoBlockWrapper, VideoPlayerWrapper } from "./ui"
import { mediaToVideoBlock } from "./utils"

const StyledDownloadLimit = styled(Row)`
  padding-top: 0.3rem;
  padding-bottom: 0.3rem;
  text-align: center;

  .ContentText.ql-editor {
    font-style: italic;
    font-size: 0.7em;
    ${sansSerifStyles({ size: 14 })}
  }
`

const DownloadLimit = ({ color }) => (
  <StyledDownloadLimit>
    <Content.Text>
      <Text.Body style={{ color }}>
        Max video upload size is 1.5 gigabytes.
      </Text.Body>
    </Content.Text>
  </StyledDownloadLimit>
)

const VideoEdit = ({ layout, blockStyles, data, onChange }) => {
  const { allowDownloadableVideos, contextKey, userID, accountID } =
    useBlocksConfig()
  const { blocksContext } = useSyncer()
  const mediaStoragePath = blocksContext?.mediaStoragePath
  const [currentSource, setCurrentSource] = React.useState(data)

  const handleUpdateSource = (updatedSource) => {
    setCurrentSource(updatedSource)

    onChange({
      ...updatedSource,
      muxVideoID: updatedSource.muxVideoID || ""
    })
  }

  const handleChangeVideo = (mediaItem) => {
    const updatedSource = mediaToVideoBlock(mediaItem, currentSource)
    handleUpdateSource(updatedSource)
  }

  const handleAddCoverImage = (mediaItem) => {
    const updatedSource = {
      ...currentSource,
      coverImage: mediaItem.url
    }

    handleUpdateSource(updatedSource)
  }

  const handleRemoveCoverImage = (e) => {
    e.stopPropagation()
    e.preventDefault()

    const updatedSource = {
      ...currentSource,
      coverImage: ""
    }

    handleUpdateSource(updatedSource)
  }

  const handleToggleDownloadable = () => {
    const allowDownload = !currentSource.allowDownload

    const nextSource = { ...currentSource, allowDownload }

    setCurrentSource(nextSource)
    onChange({ allowDownload })
  }

  const {
    downloadURL,
    streamURL,
    coverImage,
    originalThumb,
    type,
    height,
    width,
    length,
    allowDownload,
    muxVideoID
  } = currentSource

  const customTextColor = blockStyles ? blockStyles.styleAttributes.color : ""

  return (
    <VideoBlockWrapper layout={layout} blockStyles={blockStyles}>
      <DownloadLimit color={customTextColor} />

      <VideoPlayerWrapper>
        <Row
          style={{
            justifyContent: "center",
            position: "absolute",
            top: "0.5rem",
            zIndex: 1
          }}
        >
          <MediaManager
            contextKey={contextKey}
            mediaStoragePath={mediaStoragePath}
            mediaType="video"
            onChooseMedia={handleChangeVideo}
            userID={userID}
            accountID={accountID}
            allowDownloadableVideos={allowDownloadableVideos}
            renderButton={({ onClick }) => (
              <Button
                styleType="primary"
                size="medium"
                onClick={onClick}
                className="EditButton mm--replace-video"
                style={{ marginTop: "20px", color: "#fff" }}
                preIcon="shuffle"
                label="Replace Video"
              />
            )}
            className="mm--replace-video"
          />
          {!!coverImage ? (
            <Button
              style={{ marginTop: "20px" }}
              styleType="secondary"
              className="mm--cover-image EditButton"
              onClick={handleRemoveCoverImage}
            >
              Remove Cover Image
            </Button>
          ) : (
            <MediaManager
              contextKey={contextKey}
              mediaStoragePath={mediaStoragePath}
              mediaType="image"
              onChooseMedia={handleAddCoverImage}
              userID={userID}
              accountID={accountID}
              renderButton={({ onClick }) => (
                <Button
                  onClick={onClick}
                  styleType="secondary"
                  size="medium"
                  style={{ marginTop: "20px" }}
                  className="mm--cover-image EditButton"
                >
                  Add a Cover Image
                </Button>
              )}
            />
          )}
        </Row>
        {!!muxVideoID ? (
          <MuxVideoEditor
            video={{ id: muxVideoID }}
            coverImage={coverImage}
            allowDownload={allowDownloadableVideos || downloadURL}
          />
        ) : (
          // These are legacy/Filestack videos
          <LazyVideoPlayer
            downloadURL={downloadURL}
            allowDownload={true}
            // Disabling Filestack streaming b/c of Mux
            // src={streamURL}
            src={downloadURL}
            coverImage={coverImage || originalThumb}
            type={type}
            height={height}
            width={width}
            length={length}
            fullWidth={layout === "fullwidth"}
          />
        )}
      </VideoPlayerWrapper>
      {allowDownloadableVideos ? (
        <Row style={{ paddingTop: "0.3rem", paddingBottom: "0.3rem" }}>
          <MuxDownloadButton
            id={muxVideoID}
            downloadURL={downloadURL}
            renderButton={({ onClick, isLoading }) => {
              // If a Designer is enabling download on this video
              // we need to maybe trigger the download URL generation process.
              const handleToggle = () => {
                if (!currentSource.allowDownload) {
                  onClick({ downloadVideo: false })
                }
                handleToggleDownloadable()
              }

              return (
                <AllowDownload
                  allowDownload={allowDownload}
                  onChange={handleToggle}
                  textColor={customTextColor}
                  loading={isLoading}
                />
              )
            }}
          />
        </Row>
      ) : null}
    </VideoBlockWrapper>
  )
}

export default VideoEdit
